import { FC, useState } from 'react';
import type { AxiosPromise } from 'axios';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';
import { alpha, Avatar, Box, Button, CircularProgress, Link, Paper, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { ChangeHistory } from '@api/models/common/ChangeHistory';
import { ChangeSet } from '@api/models/common/ChangeSet';
import { FieldChange } from '@api/models/common/FieldChange';
import type { Args, AxiosConfig } from '@api/services/BaseService';
import { formatLocalized } from '../../../utils/dateFormat';
import { PathKey, useNavigation } from '../../../utils/urlPaths';
import { useAppSelector } from '../../../store';
import { selectIdentity } from '../../../store/entities/identity';
import { getChangeActorPath } from './getChangeActorPath';
import { formatChangeFieldLabel, formatChangeValue, isEmptyChangeValue } from './formatChangeValue';

/** Most changes the backend returns for one detail; asking for more is clamped. */
const MAX_LIMIT = 50;

export interface ChangeHistorySource {
  getById: (id: string, args?: Args, options?: AxiosConfig) => AxiosPromise<{ changes?: ChangeHistory }>;
}

interface Props {
  /** The `changes` of the loaded detail. */
  history?: ChangeHistory;
  /** The service that reads the entity detail (and with it the history), and the id of the entity. */
  service: ChangeHistorySource;
  id: string;
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** Old and new value as tinted chips (red struck-through, green), the way a diff reads at a glance. */
const FieldRow: FC<{ change: FieldChange; entity: string }> = ({ change, entity }) => {
  const { t, i18n } = useTranslation();
  const previous = formatChangeValue(change, change.previous, t, i18n);
  const next = formatChangeValue(change, change.new, t, i18n);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '12rem 1fr' },
        columnGap: 2,
        rowGap: 0.75,
        py: 1.25,
        alignItems: 'center',
        '& + &': { borderTop: 1, borderColor: 'divider' },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {formatChangeFieldLabel(change.field, entity, t, i18n)}
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.25, rowGap: 0.75, minWidth: 0 }}>
        {previous === null ? (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        ) : (
          <Typography
            component="del"
            variant="body2"
            sx={(theme) => ({
              px: 1,
              py: 0.25,
              borderRadius: 1,
              color: 'error.dark',
              bgcolor: alpha(theme.palette.error.main, 0.1),
              textDecorationColor: alpha(theme.palette.error.main, 0.6),
              overflowWrap: 'anywhere',
            })}
          >
            {previous}
          </Typography>
        )}
        <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.disabled' }} aria-hidden />
        {next === null ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {t('changeHistory.cleared')}
          </Typography>
        ) : (
          <Typography
            component="ins"
            variant="body2"
            sx={(theme) => ({
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontWeight: 500,
              textDecoration: 'none',
              color: 'success.dark',
              bgcolor: alpha(theme.palette.success.main, 0.12),
              overflowWrap: 'anywhere',
            })}
          >
            {next}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const ChangeItem: FC<{ change: ChangeSet; entity: string }> = ({ change, entity }) => {
  const { t, i18n } = useTranslation();
  const { getPath, currentPath } = useNavigation();
  const identity = useAppSelector(selectIdentity);
  const actorPath = getChangeActorPath(change, identity.roles, (userId) => getPath(PathKey.UserDetail, { userId }, { backTo: currentPath }));
  const fields = change.fields.filter((field) => !(isEmptyChangeValue(field.previous) && isEmptyChangeValue(field.new)));

  let actor = t('changeHistory.system');
  let icon = <SettingsSuggestOutlinedIcon fontSize="small" />;
  if (change.by) {
    const name = change.by.name ?? '';
    actor = change.byApiClient ? t('integration.apiAccess.changedBy', { name }) : name;
    icon = change.byApiClient ? <SyncAltIcon fontSize="small" /> : <>{initials(name)}</>;
  }

  return (
    <Paper component="li" variant="outlined" sx={{ listStyle: 'none', overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: 1.5,
          px: 2,
          py: 1.25,
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8125rem', fontWeight: 600, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          {icon}
        </Avatar>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ overflowWrap: 'anywhere' }}>
            {actorPath ? (
              <Link component={RouterLink} to={actorPath} color="inherit" underline="hover">
                {actor}
              </Link>
            ) : (
              actor
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatLocalized(change.at, 'dateTime', i18n)}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ px: 2 }}>
        {fields.map((field) => (
          <FieldRow key={field.field} change={field} entity={entity} />
        ))}
      </Box>
    </Paper>
  );
};

/** "Who changed what, when" for an entity detail: every change the backend sent, and a button for up to the backend maximum. */
export const ChangeHistoryPanel: FC<Props> = ({ history, service, id }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  // Remembers which `history` the loaded result belongs to, so a reloaded detail (after a save) wins over a stale result.
  const [loaded, setLoaded] = useState<{ base?: ChangeHistory; value: ChangeHistory }>();

  const current = loaded && loaded.base === history ? loaded.value : history;
  if (!current?.items.length) {
    return (
      <Paper
        variant="outlined"
        sx={(theme) => ({
          maxWidth: theme.breakpoints.values.md,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 5,
          borderStyle: 'dashed',
          bgcolor: 'transparent',
          color: 'text.secondary',
        })}
      >
        <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled' }} aria-hidden />
        <Typography variant="body2" sx={{ textAlign: 'center' }}>
          {t('changeHistory.empty')}
        </Typography>
      </Paper>
    );
  }

  const { entity, items, hasMore } = current;
  const canLoadMore = hasMore && items.length < MAX_LIMIT;
  const truncated = hasMore && !canLoadMore;

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      // Behind the open detail: its loader and unsaved form must not be touched.
      const response = await service.getById(id, { changesLimit: MAX_LIMIT }, { background: true });
      const value = response.data.changes;
      if (value) setLoaded({ base: history, value });
    } catch {
      // BaseService already reported the error; keep showing what we have.
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="section" sx={(theme) => ({ maxWidth: theme.breakpoints.values.md })}>
      <Box component="ol" sx={{ display: 'flex', flexDirection: 'column', gap: 2, m: 0, p: 0 }}>
        {items.map((change, index) => (
          <ChangeItem key={`${change.at}-${index}`} change={change} entity={entity} />
        ))}
      </Box>
      {(canLoadMore || truncated) && (
        <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
          {canLoadMore && (
            <Button
              variant="outlined"
              disabled={loading}
              onClick={handleLoadMore}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <ExpandMoreIcon />}
            >
              {t('changeHistory.loadOlder')}
            </Button>
          )}
          {truncated && (
            <Typography variant="body2" color="text.secondary">
              {t('changeHistory.truncated')}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
